import { useState, useEffect } from 'react'
import { useLocation, useNavigate } from 'react-router'
import { LocaleLink as Link } from '@/components/LocaleLink'
import { useHeader } from '@/lib/cms/hooks'
import { analytics } from '@/lib/analytics'
import { useContactEmail } from '@/lib/useContactEmail'
import { useTranslation } from '@/lib/i18n/LocaleContext'
import { localePath, stripLocale } from '@/lib/i18n/localePath'
import { LOCALES, messages } from '@/lib/i18n/messages'
import { SITE_NAME } from '@/site.config'

/**
 * Locale switcher. Swaps the `/:lang` prefix on the CURRENT path so the
 * visitor stays on the same page in the other language; content re-fetches
 * with the new `?locale=`.
 */
function LanguageSwitcher() {
  const { t, locale } = useTranslation()
  const location = useLocation()
  const navigate = useNavigate()
  const barePath = stripLocale(location.pathname)

  return (
    <div className="lang-switch" role="group" aria-label={t.nav.language}>
      {LOCALES.map((code) => (
        <button
          key={code}
          type="button"
          onClick={() => navigate(localePath(barePath, code))}
          aria-pressed={code === locale}
          aria-label={messages[code].nav.switchTo}
          lang={code}
        >
          {code.toUpperCase()}
        </button>
      ))}
    </div>
  )
}

function NavLinks({ links, current }: { links: { label: string; href: string }[]; current: string }) {
  return (
    <>
      {links.map((item) => (
        <Link key={item.href} to={item.href} className="nav-link" aria-current={current === item.href ? 'page' : undefined}>
          {item.label}
        </Link>
      ))}
    </>
  )
}

export function SiteHeader() {
  const { t } = useTranslation()
  const { data } = useHeader()
  const { mailto, onCtaClick } = useContactEmail()
  const location = useLocation()
  const barePath = stripLocale(location.pathname)
  const navLinks = data?.nav ?? []
  const ctaLabel = data?.ctaLabel ?? t.nav.cta
  const ctaHref = data?.ctaHref ?? mailto()
  // The drawer remembers the path it was opened on, so navigating closes it
  // without an effect; Escape closes it too.
  const [openOn, setOpenOn] = useState<string | null>(null)
  const open = openOn === location.pathname
  const setOpen = (next: boolean) => setOpenOn(next ? location.pathname : null)
  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpenOn(null)
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open])

  const cta = ctaHref ? (
    <a href={ctaHref} className="btn btn-primary btn-small" onClick={() => { analytics.ctaClick(ctaLabel, ctaHref); if (ctaHref.startsWith('mailto:')) onCtaClick() }}>
      {ctaLabel}
    </a>
  ) : null

  return (
    <>
      <a href="#main-content" className="skip-link">{t.nav.skipLink}</a>
      <header className="header">
        <div className="wrap header-inner">
          <Link to="/" className="wordmark" aria-label={t.nav.logoLabel}>{SITE_NAME}</Link>

          <nav aria-label={t.nav.primary} className="nav-desktop">
            <NavLinks links={navLinks} current={barePath} />
          </nav>

          <div className="nav-desktop header-actions">
            <LanguageSwitcher />
            {cta}
          </div>

          <button
            type="button"
            className="menu-button"
            onClick={() => { if (!open) analytics.mobileMenuOpen(); setOpen(!open) }}
            aria-expanded={open}
            aria-controls="mobile-drawer"
          >
            {open ? t.nav.closeMenu : t.nav.openMenu}
          </button>
        </div>

        {open && (
          <div id="mobile-drawer" className="drawer">
            <nav aria-label={t.nav.mobile} className="drawer-nav">
              <NavLinks links={navLinks} current={barePath} />
            </nav>
            <LanguageSwitcher />
            {cta}
          </div>
        )}
      </header>
    </>
  )
}
